import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";
import axios from "axios";

export interface PostalPreviewRecipient {
  userId: number;
  name: string;
  pages: number;
  grossCents: number;
}

export interface PostalPreview {
  postCount: number;
  emailCount: number;
  totalPages: number;
  exact: boolean;
  unitPriceGrossCents: number;
  vatRate: number;
  pricedAt: string;
  estimatedGrossCents: number;
  estimatedNetCents: number;
  estimatedTaxCents: number;
  perRecipient: PostalPreviewRecipient[];
}

interface LetterDispatchState {
  preview: PostalPreview | null;
  status: "idle" | "loading" | "failed";
  error?: string;
}

const initialState: LetterDispatchState = {
  preview: null,
  status: "idle",
};

interface ApiError {
  message: string;
}

export const previewInvoiceBatch = createAsyncThunk<
  PostalPreview,
  { ids: number[]; associationId: number },
  { rejectValue: ApiError }
>(
  "letterDispatch/previewInvoiceBatch",
  async ({ ids, associationId }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get<PostalPreview>(
        "/invoices/batch/preview",
        { params: { ids: ids.join(","), associationId } }
      );
      return res.data;
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          (err.response?.data as ApiError) ?? { message: err.message }
        );
      }
      throw err;
    }
  }
);

export const previewMeetingInvites = createAsyncThunk<
  PostalPreview,
  { meetingId: number; associationId: number },
  { rejectValue: ApiError }
>(
  "letterDispatch/previewMeetingInvites",
  async ({ meetingId, associationId }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get<PostalPreview>(
        `/meetings/${meetingId}/invite-preview`,
        { params: { associationId } }
      );
      return res.data;
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          (err.response?.data as ApiError) ?? { message: err.message }
        );
      }
      throw err;
    }
  }
);

const slice = createSlice({
  name: "letterDispatch",
  initialState,
  reducers: {
    clearPreview(state) {
      state.preview = null;
      state.status = "idle";
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(previewInvoiceBatch.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(
        previewInvoiceBatch.fulfilled,
        (state, action: PayloadAction<PostalPreview>) => {
          state.status = "idle";
          state.preview = action.payload;
        }
      )
      .addCase(previewInvoiceBatch.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message ?? "Esikatselun haku epäonnistui";
      })
      .addCase(previewMeetingInvites.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(
        previewMeetingInvites.fulfilled,
        (state, action: PayloadAction<PostalPreview>) => {
          state.status = "idle";
          state.preview = action.payload;
        }
      )
      .addCase(previewMeetingInvites.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message ?? "Esikatselun haku epäonnistui";
      });
  },
});

export const { clearPreview } = slice.actions;
export default slice.reducer;
